import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const SectionTitle = ({ tag, title }) => (
  <div className="mb-3">
    <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{tag}</div>
    <div className="font-display font-black text-2xl tracking-tight">{title}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2">
    {(items || []).map((x, i) => (
      <li key={`${i}-${x}`} className="text-sm leading-relaxed flex gap-2">
        <span className="text-primary font-mono text-xs mt-1">▸</span>
        <span>{x}</span>
      </li>
    ))}
  </ul>
);

export default function BrandProfile() {
  const [brand, setBrand] = useState(null);
  const [regen, setRegen] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/brand");
    setBrand(data);
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setRegen(true);
    toast.message("Rebuilding your Brand Intelligence Profile…", { description: "30–90 seconds." });
    try {
      await runTask("/api/brand/generate", null, { intervalMs: 2500, maxMs: 240000 });
      await load();
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setRegen(false); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post("/api/brand/approve");
      await load();
      toast.success("Approved — this is your marketing brain");
    } catch (e) { toast.error("Failed"); }
    finally { setApproving(false); }
  };

  if (!brand) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  const p = brand.profile;

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Brand Intelligence Profile</div>
            <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Your marketing brain.</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="btn-regen-bip" onClick={regenerate} disabled={regen}>
              {regen ? <><Loader2 className="h-4 w-4 animate-spin"/> Rebuilding…</> : <><Sparkles className="h-4 w-4"/> Regenerate</>}
            </Button>
            {!brand.approved && p && (
              <Button data-testid="btn-approve-bip" onClick={approve} disabled={approving}>
                {approving ? "Approving…" : <><Check className="h-4 w-4"/> This is who we are</>}
              </Button>
            )}
            {brand.approved && (
              <span className="pillar-badge text-primary border-primary" data-testid="bip-approved-badge"><Check className="h-3 w-3"/> Approved</span>
            )}
          </div>
        </div>
      </div>

      {!p ? (
        <div className="p-10">
          <div className="max-w-2xl">
            <div className="text-muted-foreground">Your onboarding intake is saved but the profile hasn't been synthesized yet.</div>
            <Button className="mt-4" onClick={regenerate} disabled={regen} data-testid="btn-generate-bip">
              {regen ? <><Loader2 className="h-4 w-4 animate-spin"/> Building…</> : <><Sparkles className="h-4 w-4"/> Build my Brand Intelligence Profile</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-4">
          {/* Identity + Mechanism */}
          <div className="col-span-12 lg:col-span-7 border border-border rounded p-6 bg-card" data-testid="bip-identity">
            <SectionTitle tag="01 · Identity" title="Who you are"/>
            <div className="text-lg leading-relaxed">{p.identity}</div>
            <div className="mt-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Unique mechanism</div>
              <div className="mt-2 text-base leading-relaxed">{p.unique_mechanism}</div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card" data-testid="bip-offer">
            <SectionTitle tag="02 · Offer" title="What you sell"/>
            <div className="space-y-2 text-sm">
              <div><span className="font-mono text-xs text-muted-foreground">Promise:</span> {p.offer_summary?.core_promise}</div>
              <div><span className="font-mono text-xs text-muted-foreground">Deliverables:</span> {p.offer_summary?.deliverables}</div>
              <div><span className="font-mono text-xs text-muted-foreground">Guarantee:</span> {p.offer_summary?.guarantee}</div>
              <div><span className="font-mono text-xs text-muted-foreground">Price:</span> {p.offer_summary?.price}</div>
              <div><span className="font-mono text-xs text-muted-foreground">Time to result:</span> {p.offer_summary?.time_to_result}</div>
            </div>
          </div>

          {/* ICP */}
          <div className="col-span-12 lg:col-span-7 border border-border rounded p-6 bg-card" data-testid="bip-icp">
            <SectionTitle tag="03 · Customer" title="Who buys"/>
            <div className="text-base leading-relaxed">{p.icp_snapshot}</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Core pains</div>
                <List items={p.core_pains}/>
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Core desires</div>
                <List items={p.core_desires}/>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card" data-testid="bip-objections">
            <SectionTitle tag="04 · Blockers" title="What stops the sale"/>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Objections</div>
            <List items={p.objections}/>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-6 mb-2">Limiting beliefs</div>
            <List items={p.limiting_beliefs}/>
          </div>

          <div className="col-span-12 border border-border rounded p-6 bg-card" data-testid="bip-belief-gaps">
            <SectionTitle tag="05 · Belief gaps" title="What we must move them from → to"/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(p.belief_gaps || []).map((b, i) => (
                <div key={`bg-${i}-${(b||"").slice(0,20)}`} className="border border-border rounded p-4 bg-background">
                  <div className="font-mono text-xs text-muted-foreground">Belief gap · {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-1 text-sm">{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Voice */}
          <div className="col-span-12 lg:col-span-7 border border-border rounded p-6 bg-card" data-testid="bip-voice">
            <SectionTitle tag="06 · Voice" title="How you sound"/>
            <div className="text-sm"><span className="font-mono text-xs text-muted-foreground">Tone:</span> {p.voice_rules?.tone}</div>
            <div className="text-sm mt-2"><span className="font-mono text-xs text-muted-foreground">Humor:</span> {p.voice_rules?.humor}</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Vocabulary</div>
                <div className="flex flex-wrap gap-2">
                  {(p.voice_rules?.vocabulary || []).map((v) => <span key={`voc-${v}`} className="pillar-badge">{v}</span>)}
                </div>
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Never say</div>
                <div className="flex flex-wrap gap-2">
                  {(p.voice_rules?.avoid || []).map((v) => <span key={`avoid-${v}`} className="pillar-badge text-destructive border-destructive/30">{v}</span>)}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Strong opinions</div>
              <List items={p.voice_rules?.opinions}/>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card" data-testid="bip-contrarian">
            <SectionTitle tag="07 · Contrarian" title="Take a stand"/>
            <List items={p.contrarian_takes}/>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-6 mb-2">Signature stories</div>
            <List items={p.signature_stories}/>
          </div>

          {/* Pillars */}
          <div className="col-span-12 border border-border rounded p-6 bg-card" data-testid="bip-pillars">
            <SectionTitle tag="08 · Pillars" title="Your content pillars"/>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(p.content_pillars || []).map((c, i) => (
                <div key={`pillar-${c.name || i}`} className="border border-border rounded p-4 bg-background">
                  <div className="font-display font-bold text-lg tracking-tight">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{c.why}</div>
                  <div className="mt-3">
                    <div className="font-mono text-[10px] uppercase text-muted-foreground">Angles</div>
                    <List items={c.example_angles}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Topics */}
          <div className="col-span-12 lg:col-span-7 border border-border rounded p-6 bg-card" data-testid="bip-topics-yes">
            <SectionTitle tag="09 · Green-lit" title="What we will talk about"/>
            <div className="flex flex-wrap gap-2">
              {(p.topics_greenlit || []).map((t) => <span key={`gl-${t}`} className="pillar-badge">{t}</span>)}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card" data-testid="bip-topics-no">
            <SectionTitle tag="10 · Forbidden" title="What we won't touch"/>
            <div className="flex flex-wrap gap-2">
              {(p.topics_forbidden || []).map((t) => <span key={`fb-${t}`} className="pillar-badge text-destructive border-destructive/30">{t}</span>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
